/**
 * Schedule Item Pills
 * The pill row under a schedule item's title: its labels as tinted pills, then the viewer's
 * registration state (Registered, Waitlisted, Signups closed, Sign up) when the item links to an
 * activity. Shared by every public schedule layout.
 */

import type React from "react";
import { useMemo } from "react";
import { Check } from "lucide-react";

import type { ScheduleItemRow, ScheduleLabelRow } from "../../../../_logic/types/db-types";
import { getItemLabels } from "../../../../_logic/utils/schedule-utils";
import {
  getScheduleItemRegistrationState,
  type ViewerRegistrationKeys,
} from "../../../../_logic/utils/schedule-registration-match";
import { isScheduleItemSignupClosed } from "../../../../_logic/utils/activity-signup-status";
import {
  SCHEDULE_NEUTRAL_STATUS_COLOR,
  SCHEDULE_REGISTERED_COLOR,
  SCHEDULE_WAITLISTED_COLOR,
} from "../../../../_logic/constants";
import { ScheduleLabelPill } from "./ScheduleLabelPill";

interface ScheduleItemPillsProps {
  item: ScheduleItemRow;
  labels: ScheduleLabelRow[];
  /** Viewer's confirmed registrations. `null` = no logged-in attendee. */
  viewerRegistrations: ViewerRegistrationKeys | null;
}

/**
 * Renders an item's label and registration pills.
 *
 * @param props - See ScheduleItemPillsProps.
 * @returns The pill row, or null when there is nothing to show.
 */
export function ScheduleItemPills({
  item,
  labels,
  viewerRegistrations,
}: ScheduleItemPillsProps): React.ReactElement | null {
  const itemLabels = useMemo(() => getItemLabels(item.label_ids, labels), [labels, item.label_ids]);
  const registrationState = getScheduleItemRegistrationState(item, viewerRegistrations);
  const isSignupClosed = isScheduleItemSignupClosed(item);
  // Logged-in viewers see their state or "Sign up"; everyone sees "Signups closed", matching the
  // Activities card.
  const showRegistration = item.activity_id != null && (viewerRegistrations != null || isSignupClosed);

  if (itemLabels.length === 0 && !showRegistration) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {itemLabels.map((label) => (
        <ScheduleLabelPill key={label.id} color={label.color}>
          {label.name}
        </ScheduleLabelPill>
      ))}
      {showRegistration &&
        (registrationState === "registered" ? (
          <ScheduleLabelPill color={SCHEDULE_REGISTERED_COLOR}>
            <Check className="h-3 w-3" aria-hidden="true" />
            Registered
          </ScheduleLabelPill>
        ) : registrationState === "waitlisted" ? (
          <ScheduleLabelPill color={SCHEDULE_WAITLISTED_COLOR}>Waitlisted</ScheduleLabelPill>
        ) : isSignupClosed ? (
          <ScheduleLabelPill color={SCHEDULE_NEUTRAL_STATUS_COLOR}>Signups closed</ScheduleLabelPill>
        ) : (
          <ScheduleLabelPill color={SCHEDULE_NEUTRAL_STATUS_COLOR}>Sign up</ScheduleLabelPill>
        ))}
    </div>
  );
}
